import React, { useState, useCallback, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";
import { router, useFocusEffect } from "expo-router";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import i18n from "../../locales/i18n";
import customAxios from "../../utils/customAxios";
import WebSidebar from "./WebSidebar";

// Import debt icon
import DeptImage from "../../assets/images/dept.png";
import { Image } from "react-native";

interface DebtItem {
  id_debt?: number;
  createdAt?: string;
  amount?: number;
  paidamount?: number;
  fullname?: string;
  phone?: string;
}

const WebDettesScreen: React.FC = () => {
  const { locale } = useSelector((state: RootState) => state.app);
  const [debts, setDebts] = useState<DebtItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // Check if current language is RTL (Arabic)
  const isRTL = locale === "ar" || i18n.locale === "ar";

  const fetchDebts = async () => {
    setIsLoading(true);
    try {
      // Use same endpoint as mobile: /debt
      const response = await customAxios.get("/debt", {
        withCredentials: true,
      });
      if (response.data && Array.isArray(response.data)) {
        setDebts(response.data);
      } else {
        setDebts([]);
      }
    } catch (error) {
      setDebts([]);
    } finally {
      setIsLoading(false);
    }
  };

  useFocusEffect(
    useCallback(() => {
      fetchDebts();
    }, [])
  );

  // Filter debts based on search
  const filteredDebts = searchQuery.trim()
    ? debts.filter(
        (d) =>
          d.fullname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          d.phone?.includes(searchQuery)
      )
    : debts;

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    return `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1).toString().padStart(2, "0")}/${date.getFullYear()}`;
  };

  const handleDebtPress = (debt: DebtItem) => {
    if (debt.id_debt) {
      router.push(`/(drawer)/dettes/${debt.id_debt}`);
    }
  };

  const handleAddDebt = () => {
    router.push("/(drawer)/dettes/add-dette");
  };

  const getStatus = (amount?: number, paidamount?: number) => {
    if (!amount) return { text: "-", color: "#999" };
    const paid = paidamount || 0;
    if (paid >= amount) {
      return { text: i18n.t("paid"), color: "#4CAF50" };
    } else if (paid > 0) {
      return {
        text: `${Math.round((paid / amount) * 100)}%`,
        color: "#FF9800",
      };
    }
    return { text: i18n.t("remaining"), color: "#F44336" };
  };

  return (
    <div
      key={`dettes-screen-${locale}`}
      className="flex flex-1 bg-[#F5F5F5] min-h-screen flex-row"
    >
      <WebSidebar />

      <View style={styles.mainContent}>
        <ScrollView
          style={styles.scrollView}
          showsVerticalScrollIndicator={false}
        >
          {/* Header with blue bar */}
          <View style={styles.headerSection}>
            <div
              className={`flex flex-row items-center gap-4 ${isRTL ? "justify-end flex-row-reverse" : "justify-start"}`}
            >
              <View style={styles.headerIconContainer}>
                <Image
                  source={DeptImage}
                  style={styles.headerIcon}
                  resizeMode="contain"
                />
              </View>
              <Text style={styles.headerTitle}>
                {i18n.t("credit")}
              </Text>
            </div>
            <View style={styles.headerBar} />
          </View>

          {/* Controls Row */}
          <View style={styles.controlsRow}>
            {/* Add Debt Button */}
            <TouchableOpacity style={styles.addButton} onPress={handleAddDebt}>
              <Text style={styles.addButtonIcon}>+</Text>
              <Text style={styles.addButtonText}>
                {i18n.t("adddate")}
              </Text>
            </TouchableOpacity>

            {/* Search Bar */}
            <View style={styles.searchContainer}>
              <TextInput
                style={styles.searchInput}
                placeholder={
                  i18n.t("searchadate")
                }
                value={searchQuery}
                onChangeText={setSearchQuery}
                placeholderTextColor="#999"
              />
              <Text style={styles.searchIcon}>🔍</Text>
            </View>
          </View>

          {/* Debts Table */}
          <View style={styles.tableContainer}>
            {/* Table Header */}
            <View style={styles.tableHeader}>
              <Text style={[styles.headerCell, styles.statusColumn]}>
                {i18n.t("paymentStatus")}
              </Text>
              <Text style={[styles.headerCell, styles.paidColumn]}>
                {i18n.t("paidamount")}
              </Text>
              <Text style={[styles.headerCell, styles.amountColumn]}>
                {i18n.t("quantityofdette")}
              </Text>
              <Text style={[styles.headerCell, styles.dateColumn]}>
                {i18n.t("dateofdette")}
              </Text>
              <Text style={[styles.headerCell, styles.phoneColumn]}>
                {i18n.t("phonenumber")}
              </Text>
              <Text style={[styles.headerCell, styles.nameColumn]}>
                {i18n.t("nameofdetn")}
              </Text>
            </View>

            {/* Loading State */}
            {isLoading ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="#1379A2" />
              </View>
            ) : filteredDebts.length === 0 ? (
              <View style={styles.emptyContainer}>
                <Text style={styles.emptyText}>
                  {i18n.t("nodata")}
                </Text>
              </View>
            ) : (
              /* Table Rows */
              filteredDebts.map((debt, index) => {
                const status = getStatus(debt.amount, debt.paidamount);
                return (
                  <TouchableOpacity
                    key={debt.id_debt || index}
                    style={[
                      styles.tableRow,
                      index % 2 === 0 ? styles.rowEven : styles.rowOdd,
                    ]}
                    onPress={() => handleDebtPress(debt)}
                  >
                    <View style={[styles.cellContainer, styles.statusColumn]}>
                      <View
                        style={[
                          styles.statusBadge,
                          { backgroundColor: status.color + "20" },
                        ]}
                      >
                        <Text
                          style={[styles.statusText, { color: status.color }]}
                        >
                          {status.text}
                        </Text>
                      </View>
                    </View>
                    <Text style={[styles.cell, styles.paidColumn]}>
                      {(debt.paidamount || 0).toLocaleString()}{" "}
                      {i18n.t("currency")}
                    </Text>
                    <Text style={[styles.cell, styles.amountColumn]}>
                      {(debt.amount || 0).toLocaleString()} {i18n.t("currency")}
                    </Text>
                    <Text style={[styles.cell, styles.dateColumn]}>
                      {formatDate(debt.createdAt)}
                    </Text>
                    <Text style={[styles.cell, styles.phoneColumn]}>
                      {debt.phone || "-"}
                    </Text>
                    <Text
                      style={[styles.cell, styles.nameColumn]}
                      numberOfLines={1}
                    >
                      {debt.fullname || "-"}
                    </Text>
                  </TouchableOpacity>
                );
              })
            )}
          </View>
        </ScrollView>
      </View>
    </div>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F5F5F5",
    minHeight: "100vh" as any,
  },
  containerRTL: {
    flexDirection: "row-reverse",
  } as any,
  mainContent: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
    padding: 20,
  },
  headerSection: {
    marginBottom: 20,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 16,
    paddingVertical: 16,
    paddingHorizontal: 24,
  },
  headerIconContainer: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: "#E8F4F8",
    borderWidth: 4,
    borderColor: "#1379A2",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  headerTitle: {
    fontSize: 32,
    fontWeight: "bold",
    color: "#333",
  },
  headerIcon: {
    width: 70,
    height: 70,
  },
  headerBar: {
    height: 8,
    backgroundColor: "#1379A2",
    borderRadius: 4,
    marginTop: 8,
  },
  controlsRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginBottom: 20,
  },
  addButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#333",
    borderRadius: 25,
    paddingVertical: 10,
    paddingHorizontal: 20,
    gap: 8,
  },
  addButtonIcon: {
    color: "#FFFFFF",
    fontSize: 20,
    fontWeight: "bold",
  },
  addButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "500",
  },
  searchContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E0E0E0",
    paddingHorizontal: 12,
  },
  searchInput: {
    flex: 1,
    paddingVertical: 12,
    fontSize: 18,
    textAlign: "right",
  },
  searchIcon: {
    fontSize: 20,
    marginLeft: 8,
  },
  tableContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#F8F8F8",
    paddingVertical: 16,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#E0E0E0",
  },
  headerCell: {
    fontSize: 16,
    fontWeight: "600",
    color: "#1379A2",
    textAlign: "center",
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 12,
    paddingHorizontal: 12,
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#F0F0F0",
  },
  rowEven: {
    backgroundColor: "#FFFFFF",
  },
  rowOdd: {
    backgroundColor: "#FAFAFA",
  },
  cell: {
    fontSize: 16,
    color: "#333",
    textAlign: "center",
  },
  cellContainer: {
    alignItems: "center",
    justifyContent: "center",
  },
  nameColumn: {
    flex: 2,
    textAlign: "right",
    paddingHorizontal: 8,
  },
  phoneColumn: {
    flex: 1.5,
  },
  dateColumn: {
    flex: 1.2,
  },
  amountColumn: {
    flex: 1.2,
  },
  paidColumn: {
    flex: 1.2,
  },
  statusColumn: {
    flex: 1,
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 14,
    fontWeight: "600",
  },
  loadingContainer: {
    padding: 60,
    alignItems: "center",
  },
  emptyContainer: {
    padding: 60,
    alignItems: "center",
  },
  emptyText: {
    color: "#999",
    fontSize: 18,
  },
});

export default WebDettesScreen;
